import { AbstractControl } from '@angular/forms';

/**
 * @description Helper function to validate coordinate values (latitude or longitude).
 * Supports both number and string inputs.
 * @param value - The value to validate.
 * @param min - Minimum allowed value.
 * @param max - Maximum allowed value.
 * @param regex - Regex pattern for format validation.
 * @param errorKey - Error key to return if validation fails.
 * @returns Validation error object if invalid, null if valid.
 */
const validateCoordinate = (
  value: any,
  min: number,
  max: number,
  regex: RegExp,
  errorKey: string,
): { [key: string]: boolean } | null => {
  // Reject null, undefined, or empty values
  if (value === null || value === undefined || value === '') {
    return { [errorKey]: true };
  }

  // Handle both number and string inputs
  let stringValue: string;
  let numValue: number;

  if (typeof value === 'number') {
    stringValue = String(value);
    numValue = value;
  } else {
    stringValue = String(value).trim();

    if (stringValue === '') {
      return { [errorKey]: true };
    }

    numValue = parseFloat(stringValue);

    if (isNaN(numValue)) {
      return { [errorKey]: true };
    }
  }

  if (numValue < min || numValue > max) {
    return { [errorKey]: true };
  }

  if (!regex.test(stringValue)) {
    return { [errorKey]: true };
  }

  return null;
};

/**
 * @description Validates if the input value is a valid latitude coordinate (-90 to 90).
 * Supports both number and string inputs.
 * @param control - The form control to validate.
 * @returns Validation error object if invalid, null if valid.
 */
export const latitudeValidator = (control: AbstractControl): { [key: string]: boolean } | null => {
  // Regex pattern for latitude: -90 to 90
  // Matches: -90, -90.0, -89.999, 0, 45.123456, 90, 90.0, etc.
  // Doesn't match: -91, 91, -90., 90., etc.
  const latitudeRegex = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/;

  return validateCoordinate(control.value, -90, 90, latitudeRegex, 'validLatitude');
};

/**
 * @description Validates if the input value is a valid longitude coordinate (-180 to 180).
 * Supports both number and string inputs.
 * @param control - The form control to validate.
 * @returns Validation error object if invalid, null if valid.
 */
export const longitudeValidator = (control: AbstractControl): { [key: string]: boolean } | null => {
  // Regex pattern for longitude: -180 to 180
  // Matches: -180, -180.0, -179.999, 0, 45.123456, 180, 180.0, etc.
  // Doesn't match: -181, 181, -180., 180., etc.
  const longitudeRegex = /^[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

  return validateCoordinate(control.value, -180, 180, longitudeRegex, 'validLongitude');
};
